import React from 'react';
import Axios from 'axios';
import dayjs from 'dayjs';
import Backdrop from '@mui/material/Backdrop';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Box, useTheme } from '@mui/material';
import { tokens } from '../../../theme';
import AddMemoDatePicker from './AddMemoDatePicker';
import { getAuthHeader } from '../../../utils/authToken';
import MainMemoOptionsToolbar from '../../weekly/mainMemo/MainMemoOptionsToolbar';
import sanitizeMemoHtml from '../../../utils/sanitizeMemoHtml';

/**
 * The student memo editor — title, reminder, bookmark and the rich-text body. Shared by the student card's memo
 * menu (MemoVertIcon) and the Missed Calls page's 메모 수정 link, so a memo is edited the same way from both.
 * Editing only: deleting a memo stays on the student card.
 *
 * Props: open · onClose · memo ({ id, title, content, reminder, bookmark }) · studentUuid · onSaved(response) ·
 * onError(error). onClose runs after a successful save.
 */
const MemoEditModal = ({ open, onClose, memo, studentUuid, onSaved, onError }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [reminderDate, setReminderDate] = React.useState(dayjs(memo?.reminder));
    const [title, setTitle] = React.useState(memo?.title || '');
    const [bookmark, setBookmark] = React.useState(memo?.bookmark || false);
    const [saving, setSaving] = React.useState(false);

    // Rich-text memo editor (contentEditable body + reused inline toolbar).
    const contentRef = React.useRef(null);
    const toolbarRef = React.useRef(null);

    // Seed the body with the memo's (sanitized) HTML once per opening. The modal renders through a portal, so the
    // editable box can attach after this effect runs — the flag lets whichever comes second do the seeding. Keyed
    // on opening, NOT memo.content, so a socket-driven prop update while open can't overwrite in-progress edits.
    const needsSeed = React.useRef(false);
    const seed = () => {
        if (needsSeed.current && contentRef.current && memo) {
            contentRef.current.innerHTML = sanitizeMemoHtml(memo.content);
            needsSeed.current = false;
        }
    };
    const setContentRef = (el) => { contentRef.current = el; seed(); };

    React.useEffect(() => {
        if (!open || !memo) return;
        setTitle(memo.title || '');
        setReminderDate(dayjs(memo.reminder));
        setBookmark(memo.bookmark || false);
        needsSeed.current = true;
        seed();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, memo?.id]);

    const handleMemoEdit = async (event) => {
        if (event) event.preventDefault();
        if (!memo) return;
        setSaving(true);
        try {
            const response = await Axios.put(`${process.env.REACT_APP_URL}/api/v1/students/${studentUuid}/memos/${memo.id}`, {
                title: title,
                content: contentRef.current?.innerHTML || "",
                reminder: reminderDate ? reminderDate.toDate() : null,
                bookmark: bookmark,
            }, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            });
            if (onSaved) onSaved(response);
            onClose();
        } catch (error) {
            if (onError) onError(error);
        } finally {
            setSaving(false);
        }
    };

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 600,
        bgcolor: colors.primary[600],
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
    };

    if (!memo) return null;

    return (
        <Modal
            aria-labelledby="transition-modal-title"
            aria-describedby="transition-modal-description"
            open={open}
            onClose={onClose}
            closeAfterTransition
            slots={{ backdrop: Backdrop }}
            slotProps={{
                backdrop: {
                    timeout: 500,
                },
            }}
        >
            <Fade in={open}>
                <Box sx={style}>
                    <Box sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "1rem",
                    }}>
                        <TextField
                            id="filled-basic"
                            label="Title"
                            variant="filled"
                            name='title'
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            sx={{
                                flex: "1",
                                maxWidth: "20rem",
                            }}
                        />
                        <AddMemoDatePicker reminderDate={reminderDate} setReminderDate={setReminderDate} />
                    </Box>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={bookmark}
                                onChange={(e) => setBookmark(e.target.checked)}
                                sx={{
                                    color: 'primary.main',
                                    '&.Mui-checked': {
                                        color: 'warning.main',
                                    },
                                }}
                            />
                        }
                        label="Bookmark"
                        sx={{
                            mt: 1,
                            '& .MuiFormControlLabel-label': {
                                color: 'text.primary',
                            },
                        }}
                    />
                    <Box sx={{ mt: "1rem", width: "100%" }}>
                        <Box
                            // Keep the editor's selection intact when clicking toolbar
                            // controls (Safari/Firefox don't focus buttons on click).
                            // Native controls still need mousedown.
                            onMouseDown={(e) => {
                                const tag = e.target.tagName;
                                if (tag !== "SELECT" && tag !== "OPTION" && tag !== "INPUT") {
                                    e.preventDefault();
                                }
                            }}
                        >
                            <MainMemoOptionsToolbar
                                toolbarRef={toolbarRef}
                                colors={colors}
                                isMainMemo={true}
                                inline={true}
                            />
                        </Box>
                        <Box
                            component="div"
                            contentEditable
                            suppressContentEditableWarning
                            ref={setContentRef}
                            data-placeholder="Content"
                            data-testid="memo-edit-content"
                            sx={{
                                minHeight: "160px",
                                width: "100%",
                                border: "1px solid #ccc",
                                borderTop: "none",
                                padding: "8px",
                                whiteSpace: "pre-wrap",
                                overflow: "auto",
                                textAlign: "left",
                                "&:focus": { outline: "2px solid #599DD5" },
                                "&:empty:before": {
                                    content: "attr(data-placeholder)",
                                    color: "#999",
                                },
                            }}
                        />
                    </Box>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={saving}
                        sx={{
                            marginTop: "1rem",
                            width: "10rem",
                        }}
                        onClick={handleMemoEdit}
                    >
                        Save Changes
                    </Button>
                </Box>
            </Fade>
        </Modal>
    );
};

export default MemoEditModal;
